import React from 'react';
import PropTypes from 'prop-types';
import { BiPlus, BiX } from 'react-icons/bi';
import {
  UnassignedCard,
  BundleName,
  CardTitle,
  SpecText,
  EntitlementChip,
  SpecRow,
  DetailsLink,
  RemoveButton,
  PrimaryButton,
  OutlineButton,
} from '../styled';
import { entitlementLabel, specLine } from '../entitlements';
import Chevron from '../chevron';

// One unassigned book / bundle.
//
// CTA behaviour:
//   • add-ons ticked    → "+ Add to this book" — stages that selection onto this
//                         book in frontend state only, then the CTA reverts below
//   • nothing ticked    → "Set up ›" — the commit point: creates the journal,
//                         assigns the book, assigns any staged add-ons, and opens
//                         the set-up flow
const UnassignedBookTile = ({
  entitlement,
  index,
  assignedChips,
  pendingChips,
  isAddOnsMode,
  hasSelection,
  isSettingUp,
  disabled,
  onSetUp,
  onAddToBook,
  onRemovePending,
  onDetails,
}) => {
  const spec = specLine(entitlement.entitlements);

  return (
    <UnassignedCard>
      {/* Bundle names are shown in full — never shortened to a generic label. */}
      {!!entitlement.bundleName && <BundleName>{entitlement.bundleName}</BundleName>}

      <CardTitle>{entitlement?.title}</CardTitle>

      <SpecRow>
        <SpecText>{spec}</SpecText>
        <DetailsLink type="button" onClick={() => onDetails(entitlement)}>
          Details
        </DetailsLink>
      </SpecRow>

      {/* Already persisted by the API: white chip. */}
      {assignedChips.map(chip => (
        <EntitlementChip key={chip.id}>{entitlementLabel(chip)}</EntitlementChip>
      ))}

      {/* Held in local state only: yellow chip with a Remove affordance. */}
      {pendingChips.map(chip => (
        <EntitlementChip key={chip.id} pending>
          {entitlementLabel(chip)}
          <RemoveButton type="button" onClick={() => onRemovePending(chip.id)}>
            Remove
            <BiX size={20} />
          </RemoveButton>
        </EntitlementChip>
      ))}

      {isAddOnsMode ? (
        <OutlineButton type="button"
          disabled={!hasSelection}
          onClick={() => onAddToBook(entitlement.id)}>
          <BiPlus size={20} />
          Add to this book
        </OutlineButton>
      ) : (
        <PrimaryButton
          type="button"
          disabled={disabled}
          onClick={() => onSetUp(entitlement.id)}
        >
          {isSettingUp ? 'Setting up' : 'Set up'}
          {!isSettingUp && <Chevron />}
        </PrimaryButton>
      )}
    </UnassignedCard>
  )
};

UnassignedBookTile.propTypes = {
  entitlement: PropTypes.shape({
    id: PropTypes.string.isRequired,
    bundleName: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  assignedChips: PropTypes.array,
  pendingChips: PropTypes.array,
  hasSelection: PropTypes.bool,
  isSettingUp: PropTypes.bool,
  disabled: PropTypes.bool,
  onSetUp: PropTypes.func.isRequired,
  onAddToBook: PropTypes.func.isRequired,
  onRemovePending: PropTypes.func.isRequired,
};

UnassignedBookTile.defaultProps = {
  assignedChips: [],
  pendingChips: [],
  hasSelection: false,
  isSettingUp: false,
  disabled: false,
};

export default UnassignedBookTile;
